import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Form, Modal, Space, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { FieldArray, Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";

import DynamicTextInput from "../../components/Inputs/DynamicTextInput";

import {
  createLicenseCategory,
  deleteLicenseCategory,
  getLicenseCategories,
  updateLicenseCategory,
} from "../../services/licenseCategoryService";
import { licenseCategorySchema } from "../../validators/licenseCategoryValidators";

const LicensingCategory = () => {
  const [licensingCategories, setLicensingCategories] = useState([]);
  const [filteredLicensingCategories, setFilteredLicensingCategories] =
    useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [licensingCategoryToEdit, setLicensingCategoryToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [licensingCategoryToDelete, setLicensingCategoryToDelete] =
    useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);

  useEffect(() => {
    loadLicensingCategories();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      const filtered = licensingCategories.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredLicensingCategories(filtered);
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm, licensingCategories]);

  const loadLicensingCategories = async () => {
    setLoading(true);
    try {
      const { data } = await getLicenseCategories();
      setLicensingCategories(data.data);
      setFilteredLicensingCategories(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load Licensing Categories"
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (licensingCategoryToEdit) {
      onUpdateLicensingCategory(payload, resetForm);
    } else {
      const nonEmptyLicensingCategories = payload?.licensingCategories.filter(
        (licenseCategory) => licenseCategory?.name !== ""
      );

      if (nonEmptyLicensingCategories.length <= 0) {
        toast.error("Licensing Categories cannot be empty!");

        return;
      }
      onCreateLicensingCategories(nonEmptyLicensingCategories, resetForm);
    }
  };

  const onCreateLicensingCategories = async (
    licensingCategoriesArray,
    resetForm
  ) => {
    setErrs(null);

    try {
      const promises = licensingCategoriesArray.map(async (licenseCategory) => {
        const payload = { name: licenseCategory.name };

        const { data } = await createLicenseCategory(payload);

        return data;
      });

      const createdLicenseCategories = await Promise.all(promises);

      setLicensingCategories((prev) => [...prev, ...createdLicenseCategories]);

      toast.success("Licensing Category(s) created successfully");

      setIsModalOpen(false);

      resetForm({ values: { licensingCategories: [{ name: "" }] } });

      await loadLicensingCategories();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error ||
            "Some Licensing Categories were not created, try again!"
        );
      }
    }
  };

  const handleEdit = (accreditingBody) => {
    setLicensingCategoryToEdit(accreditingBody);
    setIsModalOpen(true);
  };

  const onUpdateLicensingCategory = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalLicensingCategories = [...licensingCategories];

      const licensingCategoryIndex = originalLicensingCategories.findIndex(
        (licenseCategory) => licenseCategory.id === licensingCategoryToEdit.id
      );

      if (licensingCategoryIndex === -1)
        throw new Error("Licensing Category not found.");

      const { data } = await updateLicenseCategory(
        licensingCategoryToEdit.id,
        payload
      );

      originalLicensingCategories[licensingCategoryIndex] = data.data;

      setLicensingCategories(originalLicensingCategories);

      setLicensingCategoryToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Licensing Category updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Licensing Category not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setLicensingCategoryToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteLicensingCategory = async () => {
    const originalLicensingCategories = [...licensingCategories];

    const newLicensingCategories = originalLicensingCategories.filter(
      (licenseCategory) => licenseCategory.id !== licensingCategoryToDelete.id
    );

    setLicensingCategories(newLicensingCategories);

    try {
      await deleteLicenseCategory(licensingCategoryToDelete?.id);

      toast.success("Licensing Category deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Licensing Category not deleted, try again later"
        }`
      );

      setLicensingCategories(originalLicensingCategories);
    } finally {
      setIsDeleteModalOpen(false);

      setLicensingCategoryToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setLicensingCategoryToEdit(null);
    setLicensingCategoryToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 500,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },

    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Licensing Category">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Licensing Category">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Licensing Categories"
        subtitle1="Institutions"
        subtitle2="Licensing Categories"
      />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Licensing Categories List"
            count={filteredLicensingCategories.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Licensing Category"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredLicensingCategories.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {licensingCategoryToEdit
                    ? "Edit Licensing Category"
                    : "Add Licensing Category"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            {licensingCategoryToEdit ? (
              <Formik
                initialValues={{
                  name: licensingCategoryToEdit
                    ? licensingCategoryToEdit?.name
                    : "",
                }}
                enableReinitialize
                validationSchema={licenseCategorySchema}
                onSubmit={(values, { resetForm }) => {
                  onSubmit(values, resetForm);
                }}
              >
                {({ values, handleChange, handleBlur, errors, submitForm }) => {
                  return (
                    <Form className="space-y-5 px-4! py-4!">
                      {errs && <FormErrors errors={errs} />}

                      <TextInput
                        name="name"
                        title="Name"
                        placeholder="Enter Licensing Category"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        autoFocus
                      />

                      <div className="flex justify-end gap-2">
                        <FormCancelButton
                          text="Cancel"
                          onClick={handleCancel}
                        />
                        <FormButton
                          text="Save"
                          type="primary"
                          onClick={submitForm}
                        />
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            ) : (
              <Formik
                initialValues={{
                  licensingCategories: [{ name: "" }],
                }}
                enableReinitialize
                onSubmit={(values, { resetForm }) => {
                  onSubmit(values, resetForm);
                }}
              >
                {({ values, handleChange, handleBlur, errors, submitForm }) => (
                  <Form className="space-y-2 !px-5 pb-3">
                    {errs && <FormErrors errors={errs} />}
                    <FieldArray name="licensingCategories">
                      {({ remove, push }) =>
                        (values.licensingCategories || []).map(
                          (accreditingBody, index) => (
                            <DynamicTextInput
                              key={index}
                              index={index}
                              name={`licensingCategories.${index}.name`}
                              placeholder="Enter Licensing Category Name"
                              requiredField={true}
                              errors={errors}
                              values={values}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              push={push}
                              remove={remove}
                              pushName="licensingCategories"
                              dynamicFieldMap={{
                                auditCategories: {
                                  name: "",
                                },
                              }}
                            />
                          )
                        )
                      }
                    </FieldArray>
                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteLicensingCategory}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default LicensingCategory;
